import { Box, Checkbox, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";
import { ExpandCollapseButton } from "../../report-lists/reports/components/ExpandCollapseButton";
import type { ArtifactRow } from "./artifactRow";
import type { ArtifactActionHandlers } from "./ArtifactActions";
import type { ArtifactsTileSort } from "./artifactsListView";
import ArtifactTile from "./ArtifactTile";
import ArtifactTilesSelectionBar from "./ArtifactTilesSelectionBar";
import type { ArtifactTileGroup } from "./artifactTileGroups";
import { groupArtifactTiles } from "./artifactTileGroups";
import { tileGridSx } from "./artifactTileGrid";
import RecentArtifactsCard from "./RecentArtifactsCard";
import type { RecentArtifactTile } from "./recentArtifacts";

const toggleItem = (items: ReadonlySet<string>, item: string): ReadonlySet<string> => {
  const next = new Set(items);
  if (!next.delete(item)) {
    next.add(item);
  }
  return next;
};

interface ArtifactTileSectionProps extends ArtifactActionHandlers {
  group: ArtifactTileGroup;
  canManage: boolean;
  isCollapsed: boolean;
  selectedIds: ReadonlySet<string>;
  observerRoot?: Element;
  onToggleCollapsed: (groupId: string) => void;
  onSelectGroup: (rows: ArtifactRow[], selected: boolean) => void;
  onToggleSelected: (artifactId: string) => void;
  onToggleFavorite: (artifactId: string) => void;
}

const ArtifactTileSection = ({
  group,
  canManage,
  isCollapsed,
  selectedIds,
  observerRoot,
  onToggleCollapsed,
  onSelectGroup,
  ...tileHandlers
}: ArtifactTileSectionProps) => {
  const selectedCount = group.rows.filter((row) => selectedIds.has(row.id)).length;
  const isAllSelected = selectedCount === group.rows.length;

  return (
    <Stack spacing={1.5} useFlexGap>
      {group.label && (
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            ml: canManage ? 0 : -1,
            ".group-reveal": { visibility: "hidden" },
            "&:hover .group-reveal": { visibility: "visible" },
          }}
        >
          {canManage && (
            <Checkbox
              className={selectedCount > 0 ? undefined : "group-reveal"}
              checked={isAllSelected}
              indeterminate={selectedCount > 0 && !isAllSelected}
              onChange={(_, checked) => onSelectGroup(group.rows, checked)}
              slotProps={{ input: { "aria-label": `Select all in ${group.label}` } }}
              sx={{ ml: "3px" }}
            />
          )}
          <ExpandCollapseButton expanded={!isCollapsed} onClick={() => onToggleCollapsed(group.groupId)} />
          <Typography variant="subtitle1" sx={{ pl: 0.5 }}>
            {group.label} ({group.rows.length})
          </Typography>
        </Stack>
      )}
      {!isCollapsed && (
        <Box sx={tileGridSx}>
          {group.rows.map((row) => (
            <ArtifactTile
              key={row.id}
              artifact={row}
              canManage={canManage}
              isSelected={selectedIds.has(row.id)}
              observerRoot={observerRoot}
              {...tileHandlers}
            />
          ))}
        </Box>
      )}
    </Stack>
  );
};

interface Props extends ArtifactActionHandlers {
  rows: ArtifactRow[];
  recentTiles: RecentArtifactTile[];
  canManage: boolean;
  isGroupSelected: boolean;
  toolbar: ReactNode;
  noRowsText: string;
  sort: ArtifactsTileSort;
  onToggleFavorite: (artifactId: string) => void;
}

const ArtifactsTiles = ({
  rows,
  recentTiles,
  canManage,
  isGroupSelected,
  toolbar,
  noRowsText,
  sort,
  onToggleFavorite,
  onChangeGroup,
  onShare,
  onDelete,
}: Props) => {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<ReadonlySet<string>>(() => new Set());
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);

  const groups = useMemo(() => groupArtifactTiles(rows, sort, isGroupSelected), [rows, sort, isGroupSelected]);
  const handleToggleSelected = useCallback(
    (artifactId: string) => setSelectedIds((ids) => toggleItem(ids, artifactId)),
    []
  );

  const selectedRows = rows.filter((row) => selectedIds.has(row.id));
  const isSelecting = selectedRows.length > 0;

  const handleClearSelection = () => setSelectedIds(new Set());

  const handleToggleCollapsed = (groupId: string) => setCollapsedGroupIds((groupIds) => toggleItem(groupIds, groupId));

  const handleSelectGroup = (groupRows: ArtifactRow[], selected: boolean) =>
    setSelectedIds((ids) => {
      const next = new Set(ids);
      groupRows.forEach((row) => {
        if (selected) {
          next.add(row.id);
        } else {
          next.delete(row.id);
        }
      });
      return next;
    });

  return (
    <Stack spacing={2} useFlexGap sx={{ flex: 1, minHeight: 0 }}>
      <Box sx={{ position: "relative" }}>
        <Box sx={{ visibility: isSelecting ? "hidden" : undefined }}>{toolbar}</Box>
        {isSelecting && (
          <Box sx={{ position: "absolute", top: 0, left: 0, right: 0 }}>
            <ArtifactTilesSelectionBar
              selectedRows={selectedRows}
              isAllSelected={selectedRows.length === rows.length}
              onClear={handleClearSelection}
              onChangeGroup={onChangeGroup}
              onDelete={onDelete}
            />
          </Box>
        )}
      </Box>
      <Box
        ref={setScrollElement}
        className={isSelecting ? "is-selecting" : undefined}
        sx={{ flex: 1, minHeight: 0, overflowY: "auto", mx: -2, px: 2, pt: 0.5, pb: 3 }}
      >
        {rows.length === 0 ? (
          <Typography color="textSecondary" sx={{ pt: 6, textAlign: "center" }}>
            {noRowsText}
          </Typography>
        ) : (
          <Stack spacing={3} useFlexGap>
            {recentTiles.length > 0 && (
              <Box sx={{ mx: -2 }}>
                <RecentArtifactsCard
                  tiles={recentTiles}
                  canManage={canManage}
                  selectedIds={selectedIds}
                  observerRoot={scrollElement ?? undefined}
                  onToggleSelected={handleToggleSelected}
                  onToggleFavorite={onToggleFavorite}
                  onChangeGroup={onChangeGroup}
                  onShare={onShare}
                  onDelete={onDelete}
                />
              </Box>
            )}
            {groups.map((group) => (
              <ArtifactTileSection
                key={group.groupId}
                group={group}
                canManage={canManage}
                isCollapsed={collapsedGroupIds.has(group.groupId)}
                selectedIds={selectedIds}
                observerRoot={scrollElement ?? undefined}
                onToggleCollapsed={handleToggleCollapsed}
                onSelectGroup={handleSelectGroup}
                onToggleSelected={handleToggleSelected}
                onToggleFavorite={onToggleFavorite}
                onChangeGroup={onChangeGroup}
                onShare={onShare}
                onDelete={onDelete}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
};

export default ArtifactsTiles;
